import { IActivityDetails, IActivityMetricDetails } from "@Models/IActivityDetails";

/**
 * Gets metrics from activity metrics array by name
 */
const getMetrics = (metrics: IActivityMetricDetails[], name: string): IActivityMetricDetails[] => {
    return metrics.filter((x) => x.name === name);
};

/**
 * Gets standardized activity metrics for visualization
 */
export const getActivityMetrics = (activity: IActivityDetails) => {
    if (!activity?.activityMetrics?.length) {
        return {
            precision: 0,
            smoothness: 0,
            speed: 0
        };
    }

    const hasAngularMetrics = activity.activityMetrics.some(m => m.name.startsWith('angular-'));
    const metrics = activity.activityMetrics;

    // Speed always comes from activity.durationQuantile
    const speed = activity.durationQuantile * 100;

    // Get precision from either angular or regular metrics
    const precisionMetricName = hasAngularMetrics ? "angular-acceleration-deviation" : "acceleration-deviation";
    const precisionMetrics = getMetrics(metrics, precisionMetricName);
    const precision = precisionMetrics.length ?
        (precisionMetrics.map(x => x.quantile).reduce((prev, next) => prev + next) / precisionMetrics.length) * 100 :
        0;

    // Get smoothness from either angular or regular metrics
    const smoothnessMetricName = hasAngularMetrics ? "angular-dsj-mean" : "dsj-mean";
    const smoothnessMetrics = getMetrics(metrics, smoothnessMetricName);
    const smoothness = smoothnessMetrics.length ?
        (smoothnessMetrics.map(x => x.quantile).reduce((prev, next) => prev + next) / smoothnessMetrics.length) * 100 :
        0;

    return { precision, smoothness, speed };
};

/**
 * Hook to get metrics for MetricsPercentileGraph
 */
export const useActivityMetrics = (activity: IActivityDetails) => {
    const metrics = getActivityMetrics(activity);

    return {
        entries: [
            { type: "Speed", value: metrics.speed },
            { type: "Precision", value: metrics.precision },
            { type: "Smoothness", value: metrics.smoothness }
        ]
    };
};